// provenance/UIDsList.js
import React, { useState, useEffect } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    List, 
    ListItem, 
    ListItemText, 
    Pagination,
    useTheme,
    Chip
} from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';

const UIDsList = ({ provenanceId }) => {
    const [uids, setUids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const theme = useTheme();
    
    const itemsPerPage = 1000; // Show 50 UIDs per page

    useEffect(() => {
        const fetchUIDs = async () => {
            try {
                setLoading(true);
                const result = await dataProvider.getList('uids', {
                    pagination: { page, perPage: itemsPerPage },
                    filter: { provenance_id: provenanceId }
                });
                
                setUids(result.data);
                setTotalPages(Math.ceil(result.total / itemsPerPage));
            } catch (error) {
                console.error('Error fetching UIDs:', error);
                notify('Error loading UIDs', 'error');
                // Fallback with mock data for demonstration
                const mockUIDs = [
                    { id: 1, uid: 'no UIDs found' },
                ];
                setUids(mockUIDs);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };

        if (provenanceId) {
            fetchUIDs();
        }
    }, [provenanceId, page, dataProvider, notify]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    if (loading) {
        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main }}>
                        NFC Tag UIDs
                    </Typography>
                    <Typography>Loading UIDs...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                        NFC Tag UIDs
                    </Typography>
                    <Chip 
                        label={`${uids.length} UIDs`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                    />
                </Box>
                
                {uids.length === 0 ? (
                    <Typography color="text.secondary">
                        No UIDs found for this product.
                    </Typography>
                ) : (
                    <>
                        <List dense sx={{ 
                            maxHeight: 400, 
                            overflow: 'auto',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? '#0d1a1a'  // Match sidebar dark background
                                : '#EFF4E5'  // Match light theme background
                        }}>
                            {uids.map((item, index) => (
                                <ListItem 
                                    key={item.id || index}
                                    sx={{
                                        borderBottom: index < uids.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? theme.palette.secondary.main  // Gold accent for dark mode
                                                : theme.palette.primary.main,   // Dark green for light mode
                                            color: theme.palette.mode === 'dark'
                                                ? theme.palette.primary.contrastText
                                                : theme.palette.primary.contrastText,
                                            '& .MuiTypography-root': {
                                                color: theme.palette.primary.contrastText
                                            }
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.875rem',
                                                    color: theme.palette.mode === 'dark' 
                                                        ? theme.palette.primary.contrastText
                                                        : theme.palette.primary.main,
                                                    fontWeight: 500
                                                }}
                                            >
                                                {item.uid || item}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography 
                                                variant="caption" 
                                                sx={{
                                                    color: theme.palette.mode === 'dark'
                                                        ? theme.palette.text.secondary
                                                        : theme.palette.secondary.main
                                                }}
                                            >
                                                UID #{((page - 1) * itemsPerPage) + index + 1}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                        
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default UIDsList;